/**
 * Simulation API service.
 *
 * Centralizes all simulation-settings-related API calls with consistent error handling
 * and TypeScript types.
 *
 * @module simulationApi
 */

import { apiDelete, apiGet, apiPost, apiPut } from './apiUtils';

/**
 * A single section title entry available for assignment to a module/section.
 */
export interface SectionTitle {
  id: number;
  level: string;
  module: string;
  section: number;
  text: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Configuration for a specific module (e.g., reading, listening, ...).
 * - order: ordered list of section numbers (1..n)
 * - titles: optional map of sectionNumber → title string
 * - questions: optional map of sectionNumber → number of questions
 */
export interface ModuleSettings {
  order: number[];
  titles: Record<number, string>;
  questions: Record<number, number>;
}

/**
 * A full simulation configuration for a given level and exam type.
 */
export interface SimulationSettings {
  id?: number;
  name?: string;
  level: string;
  examType: string;
  moduleOrder: string[];
  moduleTimings: Record<string, number>;
  pauseBetweenModules: number;
  sectionSettings: Record<string, ModuleSettings>;
  totalDuration: number;
  isActive: boolean;
  isDefault: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Get all simulation settings
 *
 * @returns {Promise<SimulationSettings[]>} Array of simulation settings
 * @throws {Error} If API request fails
 */
export const getSimulationSettings = async (): Promise<SimulationSettings[]> => {
  const result = await apiGet<SimulationSettings[]>('/simulation/settings', {
    errorMessage: 'Failed to load simulation settings',
    context: 'getSimulationSettings',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to load simulation settings');
  }
  return result;
};

/**
 * Create a new simulation setting
 *
 * @param {SimulationSettings} settings - Simulation settings to create
 * @returns {Promise<SimulationSettings>} Created simulation settings
 * @throws {Error} If API request fails
 */
export const createSimulationSettings = async (
  settings: SimulationSettings,
): Promise<SimulationSettings> => {
  const result = await apiPost<SimulationSettings>(
    '/simulation/settings',
    settings,
    {
      errorMessage: 'Failed to create simulation settings',
      context: 'createSimulationSettings',
      throwOnError: true,
    },
  );
  if (!result) {
    throw new Error('Failed to create simulation settings');
  }
  return result;
};

/**
 * Update an existing simulation setting
 *
 * @param {number} id - Simulation settings ID to update
 * @param {SimulationSettings} settings - Updated simulation settings
 * @returns {Promise<SimulationSettings>} Updated simulation settings
 * @throws {Error} If API request fails
 */
export const updateSimulationSettings = async (
  id: number,
  settings: SimulationSettings,
): Promise<SimulationSettings> => {
  const result = await apiPut<SimulationSettings>(
    `/simulation/settings/${id}`,
    settings,
    {
      errorMessage: 'Failed to update simulation settings',
      context: 'updateSimulationSettings',
      throwOnError: true,
    },
  );
  if (!result) {
    throw new Error('Failed to update simulation settings');
  }
  return result;
};

/**
 * Delete a simulation setting by ID
 *
 * @param {number} id - Simulation settings ID to delete
 * @returns {Promise<void>} Resolves on successful deletion
 * @throws {Error} If API request fails
 */
export const deleteSimulationSettings = async (id: number): Promise<void> => {
  await apiDelete(`/simulation/settings/${id}`, {
    errorMessage: 'Failed to delete simulation settings',
    context: 'deleteSimulationSettings',
    throwOnError: true,
  });
};
